import { useState, useEffect, useRef } from 'react';
import AnimatedCard from "./AnimatedCard";
import { sortCards } from "../utilities/cardSort";

interface PlayerHandProps {
  cards: string[];
  direction?: 'horizontal' | 'vertical';
  playerName: string;
  position: 'top' | 'bottom' | 'left' | 'right';
  onConfirm?: (cards: string[]) => void;
  onPass?: () => void;
  isGameOver?: boolean;
}

export default function PlayerHand({
  cards,
  direction = 'horizontal',
  playerName,
  position,
  onConfirm,
  onPass,
  isGameOver = false,
}: PlayerHandProps) {
  const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);
  const [handCards, setHandCards] = useState<string[]>(cards);
  const [hasSorted, setHasSorted] = useState(false);

  const isBottom = position === 'bottom';
  const isVertical = direction === 'vertical';
  const isSide = position === 'left' || position === 'right';

  const prevCardsRef = useRef<string[]>(cards);
  const isFirstRender = useRef(true);

  const toggleCard = (idx: number) => {
    if (!isBottom) return;
    setSelectedIndexes(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  const handleConfirm = () => {
    if (isGameOver) return;
    const selected = selectedIndexes.map(i => handCards[i]);
    console.log("CONFIRM clicked, selected:", selected);
    onConfirm?.(selected);
    setSelectedIndexes([]);
  };

  const handlePass = () => {
    if (isGameOver) return;
    setSelectedIndexes([]); // clear any selected cards before passing
    onPass?.();
  };

  const handleSort = () => {
    setHandCards(prev => sortCards(prev));
    setSelectedIndexes([]);
    setHasSorted(true);
  };

  const renderName = () => {
    if (isSide) {
      return (
        <div className="text-black text-2xl font-bold text-center leading-tight whitespace-pre">
          {playerName.split('').join('\n')}
        </div>
      );
    }
    return (
      <div className="text-black text-2xl font-bold text-center whitespace-nowrap">
        {playerName}
      </div>
    );
  };

  const layoutClasses = {
    left: 'flex flex-row-reverse items-center gap-2',
    right: 'flex flex-row items-center gap-2',
    top: 'flex flex-col-reverse items-center gap-2',
    bottom: 'flex flex-col items-center gap-2',
  }[position];

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      setHandCards(cards);
      prevCardsRef.current = cards;
      return;
    }

    const prev = prevCardsRef.current;
    const isNewDeal = prev.length === 0 || cards.length > prev.length;

    if (isNewDeal) {
      // Genuinely a new hand — accept server order and reset sort state
      setHandCards(cards);
      setHasSorted(false);
    } else {
      // Cards were removed by a play — remove just those from our current
      // (possibly sorted) local order, don't touch anything else
      const removed = diffRemoved(prev, cards);
      setHandCards(current => removeCardsOnce(current, removed));
    }

    setSelectedIndexes([]);
    prevCardsRef.current = cards;
  }, [cards]);

  return (
    <>
      <div className={`${layoutClasses} relative`}>
        {renderName()}
        <div
          className="relative"
          style={{
            width: isVertical ? 100 : (cards.length - 1) * 20 + 100,
            height: isVertical ? (cards.length - 1) * 20 + 140 : 140,
          }}
        >
          {handCards.map((card, idx) => {
            const selected = selectedIndexes.includes(idx);
            const isLast = idx === handCards.length - 1;
            const isPartial = !isLast && !selected;
            const offset = idx * 20;

            return (
              <div
                key={idx}
                onClick={() => toggleCard(idx)}
                style={{
                  position: 'absolute',
                  left: isVertical ? undefined : `${offset}px`,
                  top: isVertical ? `${offset}px` : selected ? '-20px' : '0px',
                  zIndex: idx,
                  overflow: 'visible',
                  height: '140px',
                  width: '100px',
                  transition: 'top 0.2s ease',
                }}
              >
                <AnimatedCard
                  cardName={card}
                  partial={isPartial}
                  direction={direction}
                  isSelected={selected}
                />
              </div>
            );
          })}

          {/* One-time sort button — disappears after first use */}
          {isBottom && !hasSorted && (
            <button
              className="absolute px-4 py-2 bg-gray-600 text-white rounded-lg shadow z-50 whitespace-nowrap"
              style={{ top: '-45px', left: '-110px' }}
              onClick={handleSort}
            >
              Sort Cards
            </button>
          )}

          {/* Confirm button — appears when cards are selected */}
          {selectedIndexes.length > 0 && (
            <button
              className="absolute px-4 py-2 bg-blue-500 text-white rounded-lg shadow z-50"
              style={{ top: '-45px', right: '-110px' }}
              onClick={handleConfirm}
              disabled={isGameOver}
            >
              Confirm
            </button>
          )}

          {/* Pass button — always visible for bottom player */}
          {isBottom && (
            <button
              className="absolute px-4 py-2 bg-yellow-500 text-white rounded-lg shadow z-50 whitespace-nowrap"
              style={{ top: '-45px', left: '0px' }}
              onClick={handlePass}
              disabled={isGameOver}
            >
              Pass
            </button>
          )}

        </div>
      </div>
    </>
  );
}

// Removes one occurrence of each card in `removed` from `hand`, preserving order
function removeCardsOnce(hand: string[], removed: string[]): string[] {
  const result = [...hand];
  for (const card of removed) {
    const idx = result.indexOf(card);
    if (idx !== -1) result.splice(idx, 1);
  }
  return result;
}

// Multiset diff: cards present in `prev` but not accounted for in `next`
function diffRemoved(prev: string[], next: string[]): string[] {
  const nextCounts = new Map<string, number>();
  for (const c of next) nextCounts.set(c, (nextCounts.get(c) || 0) + 1);

  const removed: string[] = [];
  for (const c of prev) {
    const count = nextCounts.get(c) || 0;
    if (count > 0) {
      nextCounts.set(c, count - 1);
    } else {
      removed.push(c);
    }
  }
  return removed;
}